import { useContext } from 'react';
import Image from '@next/image';
import classNames from 'classnames';

import PlusCartIcon from 'assets/icons/plus-cart.svg';
import Tag from '@templates/Tag';
import AddToCompare from '@templates/AddToCompare';
import PossibleLink from '@components/atoms/PossibleLink';
import Button from '@components/atoms/Button';
import Typography from '@components/atoms/Typography';
import StarRating from '@components/StarRating';
import ComplexDeliveryLabel from '@components/ComplexDeliveryLabel';
import HtmlPreview from '@components/HtmlPreview';
import Attributes from '@templates/product/Attributes';
import { SkeletonImageContext } from '@providers/SkeletonImageProvider';
import getPlainText from 'utils/getPlainText';
import { useIntl } from 'utils/intl';

import Price from './components/Price';
import Props from './ProductCard.types';
import messages from './ProductCard.messages';
import classes from './ProductCard.module.scss';
import { getMappedAttributes } from './ProductCard.utils';

const displayClasses: Record<Exclude<Props['displayMode'], undefined>, string> = {
  tile: classes.tileMode,
  list: classes.listMode,
};

const ProductCard = ({
  id,
  name,
  thumbnail,
  attributes,
  buttonTitle,
  priceRrp,
  netPrice,
  grossPrice,
  tags = [],
  rating = 0,
  href,
  isEol,
  isPrerelease,
  isAvailable,
  imagePreload,
  imageLazyLoad,
  product,
  currentSupplier,
  daysToDelivery,
  addIcon: AddIcon = PlusCartIcon,
  displayMode = 'tile',
  scroll,
  isConfigurable,
  withFakeLink,
  withoutCompare,
  withoutDivider,
  onAddToCart,
}: Props): JSX.Element => {
  const intl = useIntl();
  const mappedMainAttributes = getMappedAttributes(attributes);
  const { getSkeletonImage } = useContext(SkeletonImageContext);
  const skeletonIcon = product.type ? getSkeletonImage('p_thumb_1', product.type.id) : undefined;

  const linkProps = {
    href,
    scroll,
    component: withFakeLink ? 'div' : undefined,
    title: getPlainText(name),
  } as const;

  return (
    <div
      className={classNames(classes.wrapper, displayClasses[displayMode], {
        [classes.withCompare]: !withoutCompare,
        [classes.isEol]: isEol,
      })}
    >
      {!withoutCompare && (
        <div className={classes.addToCompare}>
          <AddToCompare id={id} productTypeId={product.type.id} />
        </div>
      )}

      <PossibleLink {...linkProps} className={classes.thumb}>
        {thumbnail && (
          <Image
            layout="fill"
            objectFit="contain"
            className={classes.thumbImg}
            alt={getPlainText(name)}
            resolveConfig={{
              preset: 'p_thumb_1',
              productName: product.name,
              extension: thumbnail.extension,
              name: thumbnail.basename,
              width: thumbnail.width,
              height: thumbnail.height,
            }}
            skeletonIconWidth={100}
            skeletonIconHeight={100}
            skeletonIcon={skeletonIcon}
            skeletonIsExternal={!!skeletonIcon}
            skeletonBackground="white"
            hasPreload={imagePreload}
            hasLoading={!imagePreload && imageLazyLoad}
            hasLazyLoad={!imagePreload && imageLazyLoad}
            hasFade={!imagePreload && imageLazyLoad}
          />
        )}
      </PossibleLink>
      <div className={classes.content}>
        <div className={classes.tags}>
          {tags.map((tag) => (
            <div key={tag.id} className={classes.tag}>
              <Tag>{tag.name}</Tag>
            </div>
          ))}
        </div>

        <PossibleLink {...linkProps}>
          <Typography
            variant={displayMode === 'tile' ? 'h4' : 'body1'}
            renderAs="div"
            ellipsisLines={1}
            className={classes.name}
          >
            <HtmlPreview htmlText={name} />
          </Typography>
        </PossibleLink>
        <div className={classes.ratingWrapper}>
          <div className={classes.starRating}>
            <StarRating rating={rating} />
          </div>

          <ComplexDeliveryLabel
            className={classes.availability}
            isEol={isEol}
            isPrerelease={isPrerelease}
            isAvailable={isAvailable}
            daysToDelivery={daysToDelivery}
            currentSupplier={currentSupplier}
          />
        </div>

        <div className={classes.bottomWrapper}>
          {!!mappedMainAttributes.length && (
            <div className={classes.attributes}>
              <Attributes
                attributes={mappedMainAttributes}
                mode={displayMode === 'list' ? 'medium' : 'small'}
                classes={{ wrapper: classes.attributesWrapper }}
                display={displayMode === 'list' ? 'inline' : 'block'}
              />
            </div>
          )}

          <div className={classes.addToCartWrapper}>
            <div className={classes.priceWrapper}>
              <Price
                netPrice={netPrice}
                grossPrice={grossPrice}
                oldGrossPrice={priceRrp}
                withFrom={isConfigurable}
                mobileAlign="left"
              />
            </div>

            {!isEol && (
              <div
                className={classNames(classes.addToCart, { [classes.disabled]: !isAvailable && !isConfigurable })}
                onClick={(e) => {
                  if (onAddToCart) {
                    e.stopPropagation();
                  }
                  if (!isAvailable) {
                    e.preventDefault();
                  }
                }}
              >
                <Button
                  size="sm"
                  color={isConfigurable ? 'tertiary' : 'primary'}
                  name="add-to-cart"
                  title={buttonTitle || intl.formatMessage(messages.addToCart)}
                  disabled={!isAvailable && !isConfigurable}
                  href={isConfigurable ? href : undefined}
                  icon={<AddIcon />}
                  onClick={onAddToCart}
                  withoutIconFill={isConfigurable}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {!withoutDivider && <div className={classes.grayBar} />}
    </div>
  );
};

export default ProductCard;
